import { useTheme } from '../ThemeContext';
import { useEffect, useState, useRef } from 'react';
import ResearchBook from '../components/ResearchBook';

const LAYER_DATA = [
  { layer:'IP Layer', role:'End-to-end delay measurement', metric:'ip.in → ip.out timestamps', color:'#00d4ff' },
  { layer:'RLC Layer', role:'Frame Alignment Delay source', metric:'Scheduler periodicity', color:'#ff5577' },
  { layer:'MAC Layer', role:'Resource block allocation', metric:'Transmission scheduling', color:'#00cc66' },
  { layer:'Physical Layer', role:'Radio channel conditions', metric:'SINR, CQI, MCS index', color:'#f0a500' },
];

const TIMELINE = [
  { phase:'Data Collection', desc:'40,000 real 5G packet traces captured at KTH ExPECA testbed under live network conditions', year:'Phase 1' },
  { phase:'Layer Analysis', desc:'Identified Frame Alignment Delay in RLC layer as primary cause of triangular Delay vs. SN pattern', year:'Phase 2' },
  { phase:'Model Selection', desc:'Evaluated ARIMA, RNN, LSTM — discovered pure recursive accumulates phase drift beyond 30 steps', year:'Phase 3' },
  { phase:'Hybrid Design', desc:'Designed block-wise recursive approach: predict 10, reset with real data, repeat — drift bounded to O(10)', year:'Phase 4' },
  { phase:'Deployment', desc:'Converted Keras → ONNX via tf2onnx, reducing RAM from 450MB to 80MB for free-tier cloud deployment', year:'Phase 5' },
  { phase:'Validation', desc:'Achieved R²=0.9067, MAE=0.2009ms, Precision=1.0, Accuracy=94.68% on held-out TE1.csv', year:'Phase 6' },
];

function DNAAnimation({ dark }) {
  const accent = dark ? '#00d4ff' : '#0078c8';
  return (
    <div style={{position:'relative',width:80,height:120,margin:'0 auto'}}>
      <style>{`
        @keyframes dnaRotate{0%{transform:rotateY(0deg);}100%{transform:rotateY(360deg);}}
        @keyframes dnaFade{0%,100%{opacity:0.2;}50%{opacity:1;}}
        @keyframes dnaScan{0%{transform:translateY(0);opacity:1;}100%{transform:translateY(120px);opacity:0;}}
      `}</style>
      {/* helix strands */}
      {Array.from({length:8},(_,i)=>{
        const y = i*14+8;
        const phase = (i/8)*Math.PI*2;
        const x1 = 40 + Math.cos(phase)*28;
        const x2 = 40 + Math.cos(phase+Math.PI)*28;
        return (
          <g key={i}>
            <div style={{position:'absolute',top:y,left:x1-4,width:8,height:8,borderRadius:'50%',background:accent,boxShadow:`0 0 6px ${accent}`,animation:`dnaFade 2s ease ${i*0.25}s infinite`}}/>
            <div style={{position:'absolute',top:y,left:x2-4,width:8,height:8,borderRadius:'50%',background:'#ff5577',boxShadow:'0 0 6px #ff5577',animation:`dnaFade 2s ease ${i*0.25+1}s infinite`}}/>
            <div style={{position:'absolute',top:y+4,left:Math.min(x1,x2),width:Math.abs(x2-x1),height:1,background:`linear-gradient(90deg,${accent},#ff5577)`,opacity:0.4}}/>
          </g>
        );
      })}
      {/* scanning line */}
      <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,transparent,${accent},transparent)`,animation:'dnaScan 2s linear infinite'}}/>
    </div>
  );
}

export default function ResearchData() {
  const { dark } = useTheme();
  const [introGone, setIntroGone] = useState(false);
  const [inView, setInView] = useState({});
  const refs = useRef({});

  useEffect(() => {
    setTimeout(() => setIntroGone(true), 2800);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting) setInView(p=>({...p,[e.target.dataset.r]:true})); });
    }, {threshold:0.1});
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const bg = dark?'#060c18':'#f4f8ff';
  const border = dark?'rgba(0,212,255,0.13)':'rgba(0,120,200,0.13)';
  const accent = dark?'#00d4ff':'#0078c8';
  const text1 = dark?'#e8f4ff':'#0a1e3d';
  const text2 = dark?'#8ab4d4':'#3a5a7a';
  const text3 = dark?'#4a7090':'#7090b0';
  const gridC = dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)';
  const cardBg = dark?'#0d1929':'#ffffff';
  const cardShad = dark?'none':'0 2px 16px rgba(0,100,200,0.07)';
  const ref = k => el => { refs.current[k]=el; };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .rd-root{background:${bg};color:${text1};font-family:'Inter',sans-serif;min-height:100vh;padding-top:60px;transition:background 0.35s;}
        .rd-root::before{content:'';position:fixed;inset:0;background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);background-size:52px 52px;pointer-events:none;z-index:0;}
        .fade-in{opacity:0;transform:translateY(22px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.vis{opacity:1;transform:none;}
        .slabel{font-size:11px;font-weight:700;letter-spacing:3px;color:${text3};text-transform:uppercase;margin-bottom:16px;display:flex;align-items:center;gap:12px;}
        .slabel::after{content:'';flex:1;height:1px;background:${border};}
        .stitle{font-family:'Orbitron',monospace;font-size:clamp(20px,3vw,30px);font-weight:700;color:${text1};margin-bottom:10px;}
        .sbody{font-size:15px;color:${text2};line-height:1.8;margin-bottom:32px;max-width:660px;}
      `}</style>

      {/* DNA intro */}
      {!introGone && (
        <div style={{position:'fixed',inset:0,zIndex:9000,background:dark?'#060c18':'#f4f8ff',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:24,animation:'fadeOut 0.5s ease 2.3s forwards',pointerEvents:'none'}}>
          <style>{`@keyframes fadeOut{to{opacity:0;visibility:hidden;}}`}</style>
          <DNAAnimation dark={dark}/>
          <div style={{fontFamily:"'Orbitron',monospace",fontSize:13,letterSpacing:4,color:accent,textTransform:'uppercase'}}>Research Data</div>
          <div style={{fontSize:12,color:text3,letterSpacing:2}}>KTH ExPECA · 40K Traces · RLC Analysis</div>
        </div>
      )}

      <div className="rd-root">
        {/* Hero */}
        <div style={{position:'relative',zIndex:1,padding:'60px 24px 40px',textAlign:'center',borderBottom:`1px solid ${border}`,background:dark?'rgba(0,212,255,0.02)':'rgba(0,120,200,0.02)'}}>
          <div style={{background:dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)',border:`1px solid ${border}`,display:'inline-block',padding:'5px 18px',borderRadius:20,marginBottom:20}}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:3,color:accent,textTransform:'uppercase'}}> Research Data</span>
          </div>
          <h1 style={{fontFamily:"'Orbitron',monospace",fontSize:'clamp(24px,5vw,48px)',fontWeight:900,color:text1,marginBottom:12,lineHeight:1.08}}>
            Dataset & Methodology<br/><span style={{color:accent}}>KTH ExPECA Testbed</span>
          </h1>
          <p style={{fontSize:16,color:text2,maxWidth:540,margin:'0 auto',lineHeight:1.7}}>
            40,000 real 5G packet traces. Not simulations. Captured at KTH Royal Institute of Technology, Sweden.
          </p>
        </div>

        <div style={{position:'relative',zIndex:1,maxWidth:1060,margin:'0 auto',padding:'60px 24px 20px'}}>

          {/* Dataset facts */}
          <div data-r="df" ref={ref('df')}>
            <div className={`fade-in${inView.df?' vis':''}`}>
              <div className="slabel"> 01 · Dataset Overview</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16,marginBottom:48}}>
                {[
                  {label:'Source',value:'KTH ExPECA Testbed',sub:'Royal Institute of Technology, Sweden',icon:'🏛'},
                  {label:'Total Records',value:'40,000',sub:'Real 5G packet delay traces',icon:'📊'},
                  {label:'Training File',value:'TR1.csv',sub:'Historical delay measurements',icon:'📂'},
                  {label:'Testing File',value:'TE1.csv',sub:'Held-out evaluation set',icon:'📂'},
                  {label:'Feature Type',value:'Time Series',sub:'IP delay in milliseconds',icon:'📈'},
                  {label:'Key Pattern',value:'Triangular',sub:'Frame Alignment Delay (RLC)',icon:'🔬'},
                ].map(f=>(
                  <div key={f.label} style={{border:`1px solid ${border}`,background:cardBg,borderRadius:8,padding:20,boxShadow:cardShad,transition:'all 0.25s'}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=accent+'55';e.currentTarget.style.transform='translateY(-3px)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=border;e.currentTarget.style.transform='none';}}
                  >
                    <div style={{fontSize:24,marginBottom:10}}>{f.icon}</div>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:2,color:text3,textTransform:'uppercase',marginBottom:5}}>{f.label}</div>
                    <div style={{fontFamily:"'Orbitron',monospace",fontSize:16,fontWeight:700,color:accent,marginBottom:3}}>{f.value}</div>
                    <div style={{fontSize:12,color:text2}}>{f.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Protocol layers */}
          <div data-r="pl" ref={ref('pl')}>
            <div className={`fade-in${inView.pl?' vis':''}`}>
              <div className="slabel"> 02 · Protocol Layer Analysis</div>
              <div className="stitle">Four Layers Examined</div>
              <p className="sbody">The triangular IP delay pattern originates in the RLC layer's frame alignment mechanism — a finding that was the key to building an accurate predictive model.</p>
              <div style={{display:'flex',flexDirection:'column',gap:12,marginBottom:48}}>
                {LAYER_DATA.map((l,i)=>(
                  <div key={l.layer} style={{border:`1px solid ${l.color}33`,background:cardBg,borderRadius:8,padding:20,display:'flex',alignItems:'center',gap:20,boxShadow:cardShad,transition:'all 0.25s',transitionDelay:`${i*0.08}s`}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=l.color+'66';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=l.color+'33';}}
                  >
                    <div style={{width:4,height:48,background:l.color,borderRadius:2,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:"'Orbitron',monospace",fontSize:13,fontWeight:700,color:l.color,marginBottom:3}}>{l.layer}</div>
                      <div style={{fontSize:14,fontWeight:600,color:text1,marginBottom:2}}>{l.role}</div>
                      <div style={{fontSize:12,color:text2}}>{l.metric}</div>
                    </div>
                    {l.layer==='RLC Layer' && (
                      <div style={{fontSize:11,fontWeight:700,color:'#ff5577',background:'rgba(255,85,119,0.1)',border:'1px solid rgba(255,85,119,0.3)',padding:'4px 12px',borderRadius:12}}>PRIMARY CAUSE</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Research Timeline */}
          <div data-r="tl" ref={ref('tl')}>
            <div className={`fade-in${inView.tl?' vis':''}`}>
              <div className="slabel"> 03 · Research Timeline</div>
              <div className="stitle">From Data to Deployment</div>
              <div style={{position:'relative',marginBottom:48}}>
                <div style={{position:'absolute',left:24,top:0,bottom:0,width:2,background:`linear-gradient(180deg,${accent},transparent)`}}/>
                {TIMELINE.map((t,i)=>(
                  <div key={t.phase} style={{display:'flex',gap:20,marginBottom:24,paddingLeft:8}}>
                    <div style={{width:36,height:36,borderRadius:'50%',background:dark?'#0d1929':'#fff',border:`2px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Orbitron',monospace",fontSize:11,fontWeight:700,color:accent,flexShrink:0,zIndex:1,marginLeft:6}}>
                      {i+1}
                    </div>
                    <div style={{border:`1px solid ${border}`,background:cardBg,borderRadius:8,padding:16,flex:1,boxShadow:cardShad}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                        <div style={{fontFamily:"'Orbitron',monospace",fontSize:13,fontWeight:700,color:text1}}>{t.phase}</div>
                        <div style={{fontSize:10,color:accent,letterSpacing:1,fontWeight:600}}>{t.year}</div>
                      </div>
                      <div style={{fontSize:13,color:text2,lineHeight:1.6}}>{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Research Book */}
          <div data-r="rb" ref={ref('rb')}>
            <div className={`fade-in${inView.rb?' vis':''}`}>
              <div className="slabel"> 04 · Research Journal</div>
              <ResearchBook dark={dark}/>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
